import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Hammer, Scissors, Archive, Plus, Check, ArrowRight, Copy, Download, Trash2, 
  Eye, Heart, Target, AlertTriangle, BookOpen 
} from 'lucide-react'
import { toast } from 'sonner'
import { format } from 'date-fns'

import type { 
  Fragment, Interpretation, AmbiguityHold, StateProbe, CognitiveScarcityBudget, 
  Lens, FragmentKind, TelemetryEventType 
} from './types'
import { computeBudget, formatValence, formatAgency } from './lib/budget'
import { generateId, cn, formatDate, shortId } from './lib/utils'
import { 
  createExperiment, getExperiment, addFragment, addInterpretation, addHold, 
  logProbe, castBudget, exportExperiment, deleteExperiment, getSimilar 
} from './lib/api'

const LINKEDIN = 'https://www.linkedin.com/in/mindful-abhay/'
const BUYMECOFFEE = 'https://buymeacoffee.com/abhaypratapsingh'

const KINDS: { key: FragmentKind; label: string }[] = [
  { key: 'fact', label: 'Fact' },
  { key: 'inference', label: 'Inference' },
  { key: 'affect', label: 'Affect / Emotion' },
  { key: 'constraint', label: 'Constraint' },
  { key: 'system', label: 'System / Policy' },
  { key: 'client_voice', label: 'Client Voice' },
]

const LENSES: { key: Lens; label: string; desc: string }[] = [
  { key: 'charitable', label: 'Charitable', desc: 'Assumes best effort + dignity' },
  { key: 'structural', label: 'Structural', desc: 'Names rules, power, scarcity' },
  { key: 'client', label: 'Client-centered', desc: 'From their stated or likely view' },
  { key: 'system', label: 'System view', desc: 'How the machine sees it' },
  { key: 'critical', label: 'Critical', desc: 'Where harm or failure is visible' },
  { key: 'other', label: 'Other', desc: 'Your own frame' },
]

const ATTENTION_STATES = ['diffuse', 'focused', 'fractured', 'hypervigilant', 'steady']

function SupportLinks({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-bench-ink-muted ${compact ? 'text-xs' : 'text-sm'}`}>
      <a href={LINKEDIN} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-bench-accent transition-colors">
        LinkedIn — Abhay Pratap Singh
      </a>
      <span className="text-bench-ink-light">·</span>
      <a href={BUYMECOFFEE} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-bench-accent transition-colors">
        Buy me a coffee
      </a>
    </div>
  )
}

function PrivacyNotice({ compact = false }: { compact?: boolean }) {
  return (
    <div className="text-[11px] leading-snug text-bench-ink-light max-w-prose">
      This is a consented research-like instrument. We capture attention state, perceived agency, emotional valence, and chosen next action at your direction. 
      No names. No client PII encouraged. Full export + immediate delete available. See <a href="#privacy" className="underline">privacy</a> and the consent you accepted at start.
      <span className="ml-2 text-[10px] opacity-70">Built for care teams carrying benefits navigation load.</span>
    </div>
  )
}

export default function RepairBench() {
  const [view, setView] = useState<'landing' | 'bench' | 'closed' | 'history'>('landing')
  const [experimentId, setExperimentId] = useState<string | null>(null)
  const [ownerSecret, setOwnerSecret] = useState<string | null>(null)
  const [privateUrl, setPrivateUrl] = useState<string>('')

  // Core bench state (source of truth lives on server; this is working copy)
  const [caseLabel, setCaseLabel] = useState('')
  const [scenarioText, setScenarioText] = useState('')
  const [fragments, setFragments] = useState<Fragment[]>([])
  const [probes, setProbes] = useState<StateProbe[]>([])
  const [holds, setHolds] = useState<AmbiguityHold[]>([])
  const [budget, setBudget] = useState<CognitiveScarcityBudget | null>(null)
  const [patternConsent, setPatternConsent] = useState(false)

  // Live probe capture (the four dimensions)
  const [probeAttention, setProbeAttention] = useState('steady')
  const [probeAgency, setProbeAgency] = useState(52)
  const [probeValence, setProbeValence] = useState(0)
  const [probeNextAction, setProbeNextAction] = useState('')

  const [isCasting, setIsCasting] = useState(false)
  const [closedData, setClosedData] = useState<any>(null)
  const [similar, setSimilar] = useState<any>(null)
  const [showPrivacy, setShowPrivacy] = useState(false)

  // Consent gate state
  const [consentGiven, setConsentGiven] = useState(false)
  const [consentText, setConsentText] = useState('')

  const currentBudget = React.useMemo(() => {
    if (budget) return budget
    if (fragments.length === 0) return null
    return computeBudget(fragments, probes, holds, caseLabel || 'Untitled bench')
  }, [fragments, probes, holds, caseLabel, budget])

  // Simple persistence for resuming across reloads (device only)
  const LOCAL_KEY = 'repair_bench_current_v1'
  useEffect(() => {
    const saved = localStorage.getItem(LOCAL_KEY)
    if (saved && !experimentId) {
      try {
        const s = JSON.parse(saved)
        if (s.experimentId && s.secret) {
          // We don't auto-restore private data for safety; show resume card instead
        }
      } catch {}
    }
  }, [])

  function saveLocalSnapshot() {
    if (!experimentId) return
    localStorage.setItem(LOCAL_KEY, JSON.stringify({
      experimentId, secret: ownerSecret, caseLabel, scenarioText, at: Date.now()
    }))
  }

  // Load from server when we have id + secret (from link or after create)
  const loadFromServer = useCallback(async (id: string, secret: string) => {
    try {
      const data = await getExperiment(id, secret)
      setExperimentId(id)
      setOwnerSecret(secret)
      setCaseLabel(data.case_label || '')
      setScenarioText(data.scenario_text || '')
      setPatternConsent(!!data.pattern_consent)

      // Map server shape to local (simplified MVP mapping)
      const frags: Fragment[] = (data.fragments || []).map((f: any) => ({
        id: f.id,
        text: f.text,
        kind: f.kind,
        position: f.position,
        interpretations: (f.interpretations || []).map((i: any) => ({ id: i.id, lens: i.lens, text: i.text })),
        holds: (data.holds || []).filter((h: any) => h.target_id === f.id).map((h: any) => ({ id: h.id, targetType: h.target_type, targetId: h.target_id, note: h.note }))
      }))
      setFragments(frags)

      const prs: StateProbe[] = (data.probes || []).map((p: any) => ({
        id: p.id, ts: p.ts, stepContext: p.step_context, attentionState: p.attention_state,
        agency: p.agency, valence: p.valence, nextAction: p.next_action, note: p.note
      }))
      setProbes(prs)

      const hlds: AmbiguityHold[] = (data.holds || []).map((h: any) => ({ id: h.id, targetType: h.target_type, targetId: h.target_id, note: h.note }))
      setHolds(hlds)

      if (data.budget_json) {
        setBudget(JSON.parse(data.budget_json))
        setView('closed')
      } else {
        setView('bench')
      }
      setPrivateUrl(`${window.location.origin}/bench/${id}?k=${secret}`)
    } catch (e: any) {
      toast.error('Could not load bench: ' + (e.message || 'unknown'))
    }
  }, [])

  // Handle deep links on mount
  useEffect(() => {
    const path = window.location.pathname
    const params = new URLSearchParams(window.location.search)
    if (path.includes('/bench/')) {
      const id = path.split('/bench/')[1]?.split('?')[0]
      const k = params.get('k')
      if (id && k) {
        loadFromServer(id, k)
      }
    }
  }, [loadFromServer])

  // Consent language (plain, precise, research-like)
  const CONSENT = `CONSENT — repair bench (interpretive charity experiment)

I understand this is a single-session instrument for care teams working with benefits systems.

During this session the following will be recorded only if I choose to log them:
• Attention state (diffuse / focused / fractured / hypervigilant / steady)
• Perceived agency (0–100 slider)
• Emotional valence (−5 heavy/drained to +5 light/possible)
• Chosen next action (my own words)

Fragments I enter, interpretations I author (especially charitable and structural lenses), and explicit "ambiguity holds" (places where we deliberately refuse to force resolution) are stored so that I or my team can return to them.

Optional: I may consent to contribute only the structural shape of this session (counts, lens distribution, hold presence) to a Vectorize index so other care teams can see aggregate patterns such as "teams holding X type of ambiguity often report Y agency movement."

No client names, SSNs, addresses, or other PII are required. I will not paste identifiable details.

I can export everything as JSON at any time. I can delete the entire session immediately and irreversibly.

This data powers the Cognitive Scarcity Budget artifact for my own use and may contribute to coarse, privacy-respecting aggregate research on cognitive burden in benefits navigation work.

I am an adult care team member using this for professional reflection and craft improvement.`

  async function startNewBench() {
    if (!consentGiven) {
      toast.error('Please read and accept the consent first.')
      return
    }
    if (!caseLabel.trim()) {
      toast.error('Give this bench a short case label (e.g. "SNAP recert denial")')
      return
    }

    try {
      // For Turnstile: in real deploy the widget would provide the token. Here we pass a marker or test.
      // The worker will accept the test key or real token.
      const res = await createExperiment({
        case_label: caseLabel.trim(),
        scenario_text: scenarioText.trim() || undefined,
        turnstile_token: 'test-or-real-token-here', // UI would normally read from Turnstile widget response
      })

      setExperimentId(res.experiment_id)
      setOwnerSecret(res.owner_secret)
      setPrivateUrl(res.private_control_url)
      setConsentText(CONSENT)
      setView('bench')

      // Log an initial probe automatically (baseline)
      const baseline: StateProbe = {
        id: generateId(8),
        ts: Date.now(),
        stepContext: 'intake',
        attentionState: 'steady',
        agency: 48,
        valence: -1,
        nextAction: 'Open this file and begin mapping what we actually know vs. what we are assuming.',
      }
      setProbes([baseline])

      // Fire and forget initial probe to server
      try {
        await logProbe(res.experiment_id, res.owner_secret, {
          step_context: 'intake',
          attention_state: baseline.attentionState,
          agency: baseline.agency,
          valence: baseline.valence,
          next_action: baseline.nextAction,
        })
      } catch {}

      toast.success('Bench opened. This session is now a tiny experiment.')
      saveLocalSnapshot()
    } catch (e: any) {
      toast.error('Failed to open bench: ' + (e.message || 'check connection / Turnstile'))
    }
  }

  // Fragment actions
  async function createFragment(text: string, kind: FragmentKind) {
    if (!experimentId || !ownerSecret) return
    const tempId = 'tmp_' + generateId(6)
    const newFrag: Fragment = {
      id: tempId,
      text: text.trim(),
      kind,
      position: fragments.length,
      interpretations: [],
      holds: [],
    }
    setFragments(prev => [...prev, newFrag])

    try {
      const created = await addFragment(experimentId, ownerSecret, { text: text.trim(), kind })
      // Replace temp
      setFragments(prev => prev.map(f => f.id === tempId ? { ...f, id: created.id || f.id } : f))
      await logTelemetry('fragment.added')
    } catch (e) {
      toast.error('Fragment save failed (still in local bench)')
    }
  }

  async function addInterpToFragment(fragId: string, lens: Lens, text: string) {
    if (!experimentId || !ownerSecret) return
    const temp = { id: 'tmp_i' + generateId(5), lens, text }
    setFragments(prev => prev.map(f => 
      f.id === fragId ? { ...f, interpretations: [...f.interpretations, temp] } : f
    ))
    try {
      await addInterpretation(experimentId, ownerSecret, fragId, { lens, text })
      await logTelemetry('interpretation.added')
    } catch {}
  }

  async function createHold(targetType: AmbiguityHold['targetType'], targetId?: string, note?: string) {
    if (!experimentId || !ownerSecret) return
    const h: AmbiguityHold = { id: 'tmp_h' + generateId(5), targetType, targetId, note }
    setHolds(prev => [...prev, h])
    try {
      const res = await addHold(experimentId, ownerSecret, { targetType, targetId, note })
      setHolds(prev => prev.map(x => x.id === h.id ? { ...x, id: res.id || x.id } : x))
      await logTelemetry('hold.created')
      toast('Ambiguity held. This is data, not failure.')
    } catch {}
  }

  // The four telemetry dimensions — captured deliberately
  async function logCurrentProbe(stepContext = 'manual') {
    if (!experimentId || !ownerSecret) return
    if (!probeNextAction.trim()) {
      toast.error('A chosen next action is required for every probe (even "pause and breathe").')
      return
    }
    const p: StateProbe = {
      id: generateId(8),
      ts: Date.now(),
      stepContext,
      attentionState: probeAttention,
      agency: probeAgency,
      valence: probeValence,
      nextAction: probeNextAction.trim(),
    }
    setProbes(prev => [...prev, p])

    try {
      await logProbe(experimentId, ownerSecret, {
        step_context: stepContext,
        attention_state: p.attentionState,
        agency: p.agency,
        valence: p.valence,
        next_action: p.nextAction,
        note: p.note,
      })
      await logTelemetry('probe.logged')
      toast.success(`State logged — agency ${p.agency}%, ${formatValence(p.valence)}`)
      // Reset for next
      setProbeNextAction('')
    } catch (e) {
      toast('Probe saved locally only for now')
    }
  }

  async function logTelemetry(type: TelemetryEventType) {
    // Fire-and-forget; the worker will also write AE + D1
    try {
      await fetch('/api/events', {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-owner-secret': ownerSecret || '' },
        body: JSON.stringify({ 
          type, 
          experiment_id: experimentId,
          payload: { 
            fragments: fragments.length, 
            probes: probes.length, 
            holds: holds.length,
            has_budget: !!budget 
          } 
        })
      })
    } catch {}
  }

  // Cast the budget — the experiment close
  async function castTheBudget() {
    if (!experimentId || !ownerSecret) return
    if (fragments.length < 1) {
      toast.error('Add at least one fragment before casting.')
      return
    }
    setIsCasting(true)
    const computed = currentBudget || computeBudget(fragments, probes, holds, caseLabel)

    try {
      const res = await castBudget(experimentId, ownerSecret, {
        case_label: caseLabel,
        budget: computed,
        pattern_consent: patternConsent,
        final_probes: probes.slice(-3),
        holds_count: holds.length,
      })
      setBudget(computed)
      setClosedData(res)
      setView('closed')

      // Optional: fetch similar patterns (if vectors + consent)
      if (patternConsent) {
        try {
          const sim = await getSimilar(experimentId, ownerSecret)
          setSimilar(sim)
        } catch {}
      }

      await logTelemetry('budget.cast')
      toast.success('Cognitive Scarcity Budget cast. This is now a fixed artifact for re-weighing later.')
    } catch (e: any) {
      // Still show locally computed even if server hiccup
      setBudget(computed)
      setView('closed')
      toast.error('Server save had an issue — budget is still valid locally. Try export.')
    } finally {
      setIsCasting(false)
    }
  }

  async function doExport() {
    if (!experimentId || !ownerSecret) {
      // local only export
      const blob = new Blob([JSON.stringify({ caseLabel, scenarioText, fragments, probes, holds, budget: currentBudget }, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `repair-bench-${shortId(experimentId || 'local')}.json`
      a.click()
      return
    }
    try {
      const data = await exportExperiment(experimentId, ownerSecret)
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `repair-bench-${shortId(experimentId)}.json`
      a.click()
      toast('Full export downloaded')
    } catch {
      toast.error('Export failed')
    }
  }

  async function doDelete() {
    if (!experimentId || !ownerSecret) {
      setView('landing')
      setFragments([]); setProbes([]); setHolds([]); setBudget(null)
      return
    }
    if (!confirm('Permanently delete this entire bench and all its data? This cannot be undone.')) return
    try {
      await deleteExperiment(experimentId, ownerSecret)
      toast.success('Bench erased.')
      // Reset UI
      setView('landing')
      setExperimentId(null)
      setOwnerSecret(null)
      setFragments([])
      setProbes([])
      setHolds([])
      setBudget(null)
      setCaseLabel('')
      setScenarioText('')
      localStorage.removeItem(LOCAL_KEY)
    } catch {
      toast.error('Delete may have only happened locally')
    }
  }

  // Quick add fragment UI helper
  const [newFragText, setNewFragText] = useState('')
  const [newFragKind, setNewFragKind] = useState<FragmentKind>('inference')

  function quickAddFrag() {
    if (!newFragText.trim()) return
    createFragment(newFragText, newFragKind)
    setNewFragText('')
  }

  // Render a single fragment workbench card
  function FragmentWorkbench({ frag }: { frag: Fragment }) {
    const [interpText, setInterpText] = useState('')
    const [interpLens, setInterpLens] = useState<Lens>('charitable')
    const [holdNote, setHoldNote] = useState('')

    return (
      <div className="fragment-card rounded-xl p-4 mb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="bench-label mb-0.5">{frag.kind}</div>
            <div className="text-[15px] leading-snug pr-2">{frag.text}</div>
          </div>
          <button 
            onClick={() => createHold('fragment', frag.id, 'We are choosing not to resolve this fragment right now')}
            className="tool-btn text-xs flex items-center gap-1"
            title="Hold ambiguity on this fragment"
          >
            <AlertTriangle size={13} /> hold
          </button>
        </div>

        {/* Interpretations */}
        <div className="mt-3 space-y-2">
          {frag.interpretations.length > 0 && (
            <div className="text-[11px] text-bench-ink-light mb-1">interpretations offered</div>
          )}
          {frag.interpretations.map((i, idx) => (
            <div key={idx} className="interp pl-3 py-1.5 pr-2 rounded text-sm">
              <span className="font-medium text-bench-accent">{i.lens}</span> — {i.text}
            </div>
          ))}
        </div>

        {/* Add interpretation */}
        <div className="mt-3 flex flex-col gap-2">
          <div className="flex flex-wrap gap-1.5">
            {LENSES.map(l => (
              <button key={l.key} onClick={() => setInterpLens(l.key)}
                className={cn('tool-btn text-xs px-2.5 py-0.5', interpLens === l.key && 'active')}>
                {l.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input 
              value={interpText} 
              onChange={e => setInterpText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && interpText.trim()) { addInterpToFragment(frag.id, interpLens, interpText); setInterpText('') } }}
              placeholder="Write a deliberate re-reading..." 
              className="flex-1 bg-white border border-bench-line rounded px-3 py-1.5 text-sm placeholder:text-bench-ink-light" 
            />
            <button onClick={() => { if (interpText.trim()) { addInterpToFragment(frag.id, interpLens, interpText); setInterpText('') } }} className="tool-btn px-3">
              <Plus size={15} />
            </button>
          </div>
          <div className="text-[10px] text-bench-ink-light">Charitable + structural lenses are the core practice here.</div>
        </div>

        {/* Hold on this fragment */}
        <div className="mt-2 flex gap-2">
          <input value={holdNote} onChange={e=>setHoldNote(e.target.value)} placeholder="Why hold this? (optional)" className="flex-1 text-sm border border-bench-line rounded px-2 py-1 bg-white" />
          <button onClick={() => { createHold('fragment', frag.id, holdNote || undefined); setHoldNote('') }} className="tool-btn text-xs">hold ambiguity</button>
        </div>
      </div>
    )
  }

  // Main render
  return (
    <div className="min-h-screen bg-bench-paper text-bench-ink">
      {/* Header — workbench title + creator */}
      <div className="border-b border-bench-line bg-white/70 backdrop-blur sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Hammer className="text-bench-accent" size={22} />
              <div>
                <div className="font-semibold tracking-tight text-lg">repair bench</div>
                <div className="text-[10px] text-bench-ink-light -mt-1">world-model repair shop</div>
              </div>
            </div>
            <div className="ml-2 text-xs px-2 py-0.5 rounded bg-bench-accent/10 text-bench-accent border border-bench-accent/30">interpretive charity</div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <SupportLinks compact />
            <button onClick={() => setShowPrivacy(!showPrivacy)} className="text-xs underline text-bench-ink-light hover:text-bench-ink">privacy</button>
            {experimentId && ownerSecret && (
              <button onClick={doDelete} className="text-xs flex items-center gap-1 text-danger hover:underline"><Trash2 size={13}/> erase bench</button>
            )}
          </div>
        </div>
      </div>

      {/* Privacy expanded */}
      <AnimatePresence>
        {showPrivacy && (
          <div className="max-w-5xl mx-auto px-5 pt-4">
            <div className="consent-box p-4 text-sm rounded-xl">
              <PrivacyNotice />
              <div className="mt-3 text-xs">
                Full details in <a href="#privacy" className="underline">PRIVACY.md</a> in the source. 
                Creator: <a href={LINKEDIN} target="_blank" className="underline">Abhay Pratap Singh</a> · <a href={BUYMECOFFEE} target="_blank" className="underline">Buy me a coffee</a>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* LANDING / INTAKE */}
      {view === 'landing' && (
        <div className="max-w-3xl mx-auto px-5 pt-12 pb-24">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 text-bench-accent mb-2">
              <BookOpen size={18} /> a tiny experiment
            </div>
            <h1 className="text-4xl font-semibold tracking-tighter serif">World-model repair shop</h1>
            <p className="mt-3 text-lg text-bench-ink-muted max-w-md mx-auto">
              For care teams navigating benefits systems. Each session is deliberate interpretive charity work. 
              The output is a <span className="font-medium text-bench-ink">Cognitive Scarcity Budget</span>.
            </p>
          </div>

          {/* Consent gate — required */}
          <div className="workbench rounded-2xl p-6 mb-6">
            <div className="bench-label mb-2">Before you open the bench</div>
            <div className="text-sm leading-relaxed whitespace-pre-line text-bench-ink-muted mb-4 border-l-2 border-bench-accent pl-4">
              {CONSENT}
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={consentGiven} onChange={e => setConsentGiven(e.target.checked)} className="mt-1 accent-bench-accent" />
              <div className="text-sm">I have read the consent. I understand what will be captured (attention, agency, valence, chosen next action) and my rights to export and delete.</div>
            </label>
          </div>

          {/* Intake */}
          <div className="workbench rounded-2xl p-6 space-y-4">
            <div>
              <div className="bench-label mb-1.5">Case label (short, for your team)</div>
              <input 
                value={caseLabel} 
                onChange={e=>setCaseLabel(e.target.value)} 
                placeholder="e.g. SNAP recert — 3yo + infant, paperwork denial"
                className="w-full bg-white border border-bench-line rounded-xl px-4 py-3 text-lg focus:outline-none focus:border-bench-accent" 
              />
            </div>
            <div>
              <div className="bench-label mb-1.5">Opening scenario (what brought this to the bench)</div>
              <textarea 
                value={scenarioText} 
                onChange={e=>setScenarioText(e.target.value)}
                rows={4}
                placeholder="Client received denial notice yesterday. Caseworker said missing verification. Family has been calling daily. We have the notice + previous approval letter but no clear path to appeal timeline..."
                className="w-full bg-white border border-bench-line rounded-xl px-4 py-3 text-[15px] focus:outline-none focus:border-bench-accent resize-y" 
              />
            </div>

            <div className="pt-2 flex items-center gap-3">
              <button 
                onClick={startNewBench} 
                disabled={!consentGiven || !caseLabel.trim()}
                className="flex-1 md:flex-none bg-bench-accent hover:bg-[#6f4a22] disabled:opacity-60 text-white px-8 py-3 rounded-xl font-medium flex items-center justify-center gap-2"
              >
                Open this bench <ArrowRight size={18} />
              </button>
              <div className="text-xs text-bench-ink-light max-w-[210px]">
                Protected by Cloudflare Turnstile. Your session becomes a private experiment with a one-time secret link.
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <SupportLinks />
            <div className="mt-4 text-[11px] text-bench-ink-light">Respecting ambiguity instead of forcing resolution. Not an AI wrapper.</div>
          </div>
        </div>
      )}

      {/* THE ACTIVE BENCH — WORKSHOP UI */}
      {view === 'bench' && experimentId && (
        <div className="max-w-6xl mx-auto px-4 md:px-6 pt-6 pb-16">
          {/* Bench header */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-xs tracking-[1.5px] text-bench-ink-light">OPEN BENCH</div>
              <div className="text-2xl font-semibold tracking-tight">{caseLabel || 'Untitled experiment'}</div>
              <div className="text-bench-ink-muted text-sm mono">{shortId(experimentId)} · private</div>
            </div>
            <div className="flex gap-2 no-print">
              <button onClick={() => logCurrentProbe('manual')} className="tool-btn flex items-center gap-2"><Heart size={15}/> log state now</button>
              <button onClick={castTheBudget} disabled={isCasting || fragments.length === 0} className="tool-btn flex items-center gap-2 bg-white border-bench-accent text-bench-accent disabled:opacity-50">
                <Archive size={15} /> cast cognitive scarcity budget
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Left: Intake + Dissection */}
            <div className="lg:col-span-7">
              <div className="workbench rounded-2xl p-5 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-sm font-medium"><Scissors size={16} /> Dissection Table</div>
                  <div className="text-xs text-bench-ink-light">{fragments.length} fragments • {holds.length} holds</div>
                </div>

                {/* Add fragment */}
                <div className="flex gap-2 mb-4">
                  <select value={newFragKind} onChange={e=>setNewFragKind(e.target.value as FragmentKind)} className="bg-white border border-bench-line rounded-lg px-2 text-sm">
                    {KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                  </select>
                  <input 
                    value={newFragText} 
                    onChange={e=>setNewFragText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') quickAddFrag() }}
                    placeholder="Add an atomic piece of the current world-model..." 
                    className="flex-1 bg-white border border-bench-line rounded-lg px-3 py-2 text-sm" 
                  />
                  <button onClick={quickAddFrag} className="tool-btn"><Plus size={16} /></button>
                </div>

                {fragments.length === 0 && (
                  <div className="text-sm text-bench-ink-light py-8 text-center border border-dashed border-bench-line rounded-xl">
                    Start by adding fragments. Facts, inferences the team is carrying, affects you notice, system constraints, client voice.
                  </div>
                )}

                {fragments.sort((a,b)=>a.position-b.position).map(frag => (
                  <FragmentWorkbench key={frag.id} frag={frag} />
                ))}
              </div>

              {/* Ambiguity holds — celebrated */}
              <div className="workbench rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3 text-sm font-medium text-bench-hold">
                  <AlertTriangle size={16} /> Ambiguity Ledger — holds are capacity-preserving acts
                </div>
                {holds.length === 0 && <div className="text-sm text-bench-ink-light">No holds yet. When something resists clean resolution, hold it here deliberately.</div>}
                {holds.map((h, i) => (
                  <div key={i} className="hold rounded-lg px-3 py-2 mb-2 text-sm flex gap-2">
                    <div className="text-bench-hold mt-0.5">HOLD</div>
                    <div>{h.note || 'We are choosing not to force resolution on this element.'} <span className="text-xs text-bench-ink-light">({h.targetType})</span></div>
                  </div>
                ))}
                <button onClick={() => createHold('scenario', undefined, 'Multiple legitimate readings of the denial language coexist; we will not pick one for the client yet.')} className="mt-2 tool-btn text-xs">+ hold ambiguity on the whole scenario</button>
              </div>
            </div>

            {/* Right column: State probes + live budget preview */}
            <div className="lg:col-span-5 space-y-4">
              {/* Telemetry capture panel — the four required signals */}
              <div className="probe-panel rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Target size={16} className="text-bench-probe" />
                  <div className="font-medium text-sm">State Probe — capture the four signals</div>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="bench-label mb-1.5">Attention state right now</div>
                    <div className="flex flex-wrap gap-1.5">
                      {ATTENTION_STATES.map(s => (
                        <button key={s} onClick={() => setProbeAttention(s)} className={cn('state-chip', probeAttention === s && 'active')}>{s}</button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="bench-label mb-1">Perceived agency (0–100)</div>
                    <input type="range" min={0} max={100} value={probeAgency} onChange={e=>setProbeAgency(parseInt(e.target.value))} className="w-full accent-bench-accent" />
                    <div className="flex justify-between text-xs text-bench-ink-light mt-0.5">
                      <div>constrained</div><div className="font-medium text-bench-ink">{probeAgency}% — {formatAgency(probeAgency)}</div><div>high</div>
                    </div>
                  </div>

                  <div>
                    <div className="bench-label mb-1">Emotional valence (−5 to +5)</div>
                    <input type="range" min={-5} max={5} step={1} value={probeValence} onChange={e=>setProbeValence(parseInt(e.target.value))} className="w-full accent-bench-accent" />
                    <div className="text-center text-sm mt-0.5">{probeValence} — <span className="text-bench-ink-muted">{formatValence(probeValence)}</span></div>
                  </div>

                  <div>
                    <div className="bench-label mb-1.5">Chosen next action (your actual next step)</div>
                    <textarea value={probeNextAction} onChange={e=>setProbeNextAction(e.target.value)} rows={2} placeholder="Call the legal aid line at 2pm. Or: sit with the client for 8 minutes without problem-solving." className="w-full text-sm border border-bench-line rounded-xl p-3 bg-white" />
                  </div>

                  <button onClick={() => logCurrentProbe('probe-panel')} className="w-full py-2.5 rounded-xl bg-bench-probe text-white text-sm font-medium flex items-center justify-center gap-2">
                    Log this state <Check size={16} />
                  </button>
                  <div className="text-[10px] text-center text-bench-ink-light">Every probe is research telemetry you consented to. Multiple probes build a trajectory.</div>
                </div>
              </div>

              {/* Live / preview budget */}
              <div className="workbench rounded-2xl p-5">
                <div className="font-medium text-sm mb-3 flex items-center gap-2"><Archive size={15} /> Cognitive Scarcity Budget (live)</div>
                {!currentBudget && <div className="text-sm text-bench-ink-light">Add fragments and log at least one probe. The budget computes from your actual labor.</div>}
                {currentBudget && (
                  <div className="text-sm space-y-3">
                    <div className="flex justify-between text-xs"><div>scarcity estimate</div><div className="font-medium">{currentBudget.scarcityEstimate} units</div></div>
                    <div className="flex justify-between text-xs"><div>tolerance (ambiguity credit)</div><div className="font-medium text-bench-hold">{currentBudget.toleranceScore}</div></div>
                    
                    <div className="pt-1">
                      <div className="bench-label mb-1">allocation (your attention in this session)</div>
                      <div className="grid grid-cols-5 gap-1 text-[10px]">
                        {Object.entries(currentBudget.allocation).map(([k,v]) => (
                          <div key={k} className="bg-white border border-bench-line rounded p-1 text-center">
                            <div className="text-bench-ink-light">{k}</div>
                            <div className="font-semibold">{v}%</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="bench-label mb-1">repair actions (from what you actually did)</div>
                      <ul className="text-xs space-y-1 pl-1">
                        {currentBudget.repairActions.slice(0,3).map((a,i) => <li key={i}>• {a}</li>)}
                      </ul>
                    </div>
                    <button onClick={castTheBudget} disabled={isCasting} className="mt-2 w-full py-2 rounded-xl border border-bench-accent text-bench-accent text-sm font-medium">Cast this as the official budget for the session</button>
                  </div>
                )}
              </div>

              {/* Trajectory so far */}
              {probes.length > 0 && (
                <div className="text-xs text-bench-ink-light px-1">
                  {probes.length} probes logged. Avg agency {Math.round(probes.reduce((s,p)=>s+p.agency,0)/probes.length)}%. Last valence: {formatValence(probes[probes.length-1].valence)}.
                </div>
              )}
            </div>
          </div>

          {/* Bottom: scenario + privacy reminder */}
          {scenarioText && <div className="mt-4 text-sm text-bench-ink-muted border-l-2 border-bench-line pl-3 max-w-prose">{scenarioText}</div>}
          <div className="mt-6"><PrivacyNotice compact /></div>
        </div>
      )}

      {/* CLOSED VIEW — the artifact + share */}
      {view === 'closed' && budget && (
        <div className="max-w-3xl mx-auto px-5 pt-10 pb-20">
          <div className="text-center mb-6">
            <div className="text-bench-accent text-sm tracking-widest">EXPERIMENT CLOSED</div>
            <div className="text-3xl font-semibold tracking-tight mt-1">{caseLabel}</div>
          </div>

          {/* The Budget Card */}
          <div className="budget-surface rounded-3xl p-8 md:p-10 shadow-xl print-only:bg-white print-only:text-bench-ink print-only:border">
            <div className="text-[10px] tracking-[2px] text-white/60 mb-1 print-only:text-bench-ink-light">COGNITIVE SCARCITY BUDGET</div>
            <div className="text-2xl text-white print-only:text-bench-ink font-medium">{caseLabel}</div>
            <div className="budget-line my-4" />

            <div className="grid grid-cols-2 gap-x-8 gap-y-6 text-sm">
              <div>
                <div className="text-white/60 text-xs mb-1">SCARCITY LOAD (NOW)</div>
                <div className="text-5xl font-semibold tracking-tighter text-white print-only:text-bench-ink">{budget.scarcityEstimate}</div>
                <div className="text-white/70">units of cognitive scarcity</div>
              </div>
              <div>
                <div className="text-white/60 text-xs mb-1">AMBIGUITY TOLERANCE</div>
                <div className="text-5xl font-semibold tracking-tighter text-white print-only:text-bench-ink">{budget.toleranceScore}</div>
                <div className="text-white/70">capacity preserved by deliberate holds</div>
              </div>
            </div>

            <div className="my-6 text-white/80 text-xs">ATTENTION ALLOCATION IN THIS SESSION</div>
            <div className="grid grid-cols-5 gap-2">
              {Object.entries(budget.allocation).map(([phase, pct]) => (
                <div key={phase} className="bg-white/10 rounded-xl p-3 text-center">
                  <div className="text-[10px] text-white/60">{phase}</div>
                  <div className="text-2xl font-semibold text-white print-only:text-bench-ink">{pct}%</div>
                </div>
              ))}
            </div>

            <div className="mt-7">
              <div className="text-white/60 text-xs mb-2">REPAIR ACTIONS (SYNTHESIZED FROM YOUR LABOR)</div>
              <ul className="space-y-2 text-white print-only:text-bench-ink text-[14.5px]">
                {budget.repairActions.map((a, i) => <li key={i} className="pl-1">→ {a}</li>)}
              </ul>
            </div>

            <div className="mt-8 pt-6 border-t border-white/20 text-[12.5px] leading-relaxed text-white/90 print-only:text-bench-ink-light whitespace-pre-line">
              {budget.fullText.split('\n\n').slice(0,4).join('\n\n')}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3 mt-6 no-print">
            <button onClick={doExport} className="tool-btn flex items-center gap-2"><Download size={16} /> export full JSON</button>
            <button onClick={() => window.print()} className="tool-btn flex items-center gap-2"><Eye size={16} /> print / save PDF</button>
            <button onClick={() => { navigator.clipboard.writeText(budget.fullText); toast('Budget text copied') }} className="tool-btn flex items-center gap-2"><Copy size={16} /> copy narrative</button>
            <button onClick={() => { setView('bench'); setBudget(null) }} className="tool-btn">return to bench</button>
          </div>

          {/* Similar patterns (if consented) */}
          {similar && (
            <div className="mt-8 text-xs text-bench-ink-muted">
              Other teams have held similar structural ambiguities. Your contribution (if consented) helps surface that you are not inventing this load.
            </div>
          )}

          <div className="mt-10 text-center text-xs">
            <SupportLinks compact />
            <div className="mt-1 text-bench-ink-light">This budget is yours. Re-weigh it in a month. Delete it any time.</div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-bench-line py-5 text-center text-[11px] text-bench-ink-light mt-12">
        repair bench — a public-utility cognitive instrument for care teams. 
        Not an AI wrapper. Ambiguity is data. <a href={BUYMECOFFEE} className="underline">Support the work</a>.
      </div>
    </div>
  )
}
